/**
 * SearchableListItem Component
 *
 * Individual row in the SearchableListPanel. Displays entity name with
 * metadata, matching the NameColumn pattern from asset/vulnerability tables.
 */

import { Seeds as SeedsIcon } from '@/components/chariot-ui';
import React from 'react';

import { OverflowText } from '@/components/OverflowText';
import { StatusChip, type StatusChipVariant } from '@/components/legacy-ui/StatusChip';
import { cn } from '@/utils/classname';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import {
  assetStatusVariants,
  getVariantForStatus,
  vulnerabilityStatusVariants,
} from '@/utils/statusChip.util';

import { LIST_ITEM_HEIGHT } from './constants';

export interface SearchableListItemProps {
  /** Primary display name */
  name: string;
  /** Secondary metadata text */
  metadata?: string;
  /** Whether to show seed icon */
  showSeedIcon?: boolean;
  /** Raw status code (e.g., 'OH' for risks, 'A' for assets) */
  status?: string;
  /** Entity type for determining which variant map to use */
  entityType?: 'asset' | 'risk';
  /** Whether this item is currently selected */
  isSelected: boolean;
  /** Click handler */
  onClick: () => void;
  /** Test ID for E2E tests */
  testId?: string;
}

/**
 * Asset status code to label mapping
 */
const assetStatusLabels: Record<string, string> = {
  A: 'Active',
  D: 'Deleted',
  F: 'Frozen',
  P: 'Pending',
};

/**
 * Get StatusChip props (label and variant) based on status and entity type
 */
function getStatusChipProps(
  status: string | undefined,
  entityType: 'asset' | 'risk' | undefined
): { label: string; variant: StatusChipVariant } | null {
  if (!status || !entityType) return null;

  if (entityType === 'risk') {
    const { statusLabel } = getRiskStatusLabel(status);
    if (!statusLabel) return null;
    const variant = getVariantForStatus(
      statusLabel,
      vulnerabilityStatusVariants
    );
    return { label: statusLabel, variant };
  }

  if (entityType === 'asset') {
    const statusCode = status[0]; // First character is the status
    const label = assetStatusLabels[statusCode] ?? 'Unknown';
    const variant = getVariantForStatus(label, assetStatusVariants);
    return { label, variant };
  }

  return null;
}

/**
 * SearchableListItem displays a single selectable row in the list panel.
 *
 * Matches the visual pattern of SelectionTable from asset/vulnerability search:
 * - Primary name in brand-primary color with bold font
 * - Secondary metadata in smaller gray text
 * - Optional seed icon for assets
 * - Selection indicated by background color (bg-chariot-stroke)
 */
export const SearchableListItem = React.memo(function SearchableListItem({
  name,
  metadata,
  showSeedIcon = false,
  status,
  entityType,
  isSelected,
  onClick,
  testId,
}: SearchableListItemProps) {
  const statusChipProps = getStatusChipProps(status, entityType);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex w-full items-center gap-3 px-3 py-2 text-left transition-colors duration-150',
        'border-chariot-stroke border-b',
        'focus:outline-hidden',
        'hover:bg-chariot-background-secondary',
        isSelected && 'is-selected bg-chariot-stroke text-chariot-text-primary'
      )}
      style={{ height: `${LIST_ITEM_HEIGHT}px` }}
      data-testid={testId}
      aria-selected={isSelected}
      role="option"
    >
      {/* Content - matches NameColumn structure */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {/* Primary name row with status */}
        <div className="flex items-center gap-2">
          <OverflowText
            className="text-chariot-brand-primary group-[.is-selected]:text-chariot-text-primary truncate text-sm font-semibold"
            text={name}
          />
          {showSeedIcon && <SeedsIcon className="size-4 shrink-0" />}
          {statusChipProps && (
            <StatusChip
              label={statusChipProps.label}
              variant={statusChipProps.variant}
              className="shrink-0"
            />
          )}
        </div>

        {/* Metadata row */}
        {metadata && (
          <OverflowText
            className="text-chariot-text-secondary group-[.is-selected]:text-chariot-text-primary truncate text-xs"
            text={metadata}
          />
        )}
      </div>
    </button>
  );
});
